'use client';

import { BackupStatus, type Condition } from '@repo/api-gw-sdk';
import { createContext, useContext, useMemo, useRef, useState, type PropsWithChildren } from 'react';
import { withBackupStatusExcluded, withoutBackupStatus } from '@/components/backupStatus/backupStatusQuery';
import { toJsonText } from '@/components/queryBuilder/QueryLanguageConverter';
import { useViewParameters } from '@/contexts/navigation';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';

// Page-scoped context: what the inventory's settings panel holds, for the
// surfaces that answer to it — the panel itself, the filter bar and the download
// button, all of which render inside the inventory page.
interface InventorySettingsContextValue {
  // Whether the Unsupported backup status is offered in the inventory's filters.
  // Deliberately not persisted: the inventory always opens with unsupported
  // resources out of view, and this is the session-long opt-in to reaching them.
  includeUnsupportedResources: boolean;
  setIncludeUnsupportedResources: (include: boolean) => void;
  // Which side holds the exclusion that keeps unsupported resources out of view:
  // the filters (it is in the URL, where the user can lift it) or the view (the
  // list query adds it).
  filtersOwnUnsupportedExclusion: boolean;
}
const InventorySettingsContext = createContext<InventorySettingsContextValue | null>(null);

/** Compares queries by value, the way `setParams` spots a query change of its own. */
const queryKey = (query: Condition | undefined) => query ? toJsonText(query) : '';
export const InventorySettingsProvider = ({
  children
}: PropsWithChildren) => {
  const {
    NewFilterBar
  } = useFeatureFlags();
  const {
    query,
    setParams
  } = useViewParameters();
  const [includeUnsupportedResources, setInclude] = useState(false);
  const handedOverToFiltersRef = useRef(false);
  const writtenQueryRef = useRef<Condition>(undefined);

  // The settings toggle that turns this on ships with the new filter bar. Without
  // it there is nothing to opt in with, so the old bar keeps its behaviour: the
  // status is offered in the filters and is hidden only by whatever exclusion the
  // query itself carries.
  const includeUnsupported = NewFilterBar ? includeUnsupportedResources : true;

  // Switching the setting writes the query the filters should hold, and the state
  // above beats that write to the reader by a render: `useSearchParams` only
  // updates once the router has processed the history entry. So the handover waits
  // for the write to arrive — otherwise that one render has neither side excluding
  // the status and the list fetches rows the user never asked to see.
  //
  // Waiting on the write rather than on the status appearing in the query is what
  // makes it arrive at all: turning the setting on while a positive status filter
  // is up writes no exclusion, because "is one of PROTECTED" already leaves
  // unsupported out, and a handover that waited to see the status would never
  // happen. Once handed over it stays that way — the user lifting the exclusion
  // takes the status back out of the query, and that must not pull it back here.
  //
  // Resolved as the render goes rather than in an effect: switching the setting off
  // has to take the exclusion back in the same render that stops trusting the
  // filters, or that render is the mirror of the gap above.
  if (!includeUnsupported) {
    handedOverToFiltersRef.current = false;
  } else if (writtenQueryRef.current === undefined || queryKey(query) === queryKey(writtenQueryRef.current)) {
    handedOverToFiltersRef.current = true;
  }
  const setIncludeUnsupportedResources = (include: boolean) => {
    const next = include ? withBackupStatusExcluded(query, BackupStatus.Unsupported) : withoutBackupStatus(query, BackupStatus.Unsupported);

    // Held so the handover above can tell this write landing from the user's own
    // later edits, which is the difference between the filters owning the exclusion
    // and the user having lifted it.
    writtenQueryRef.current = include ? next : undefined;
    setInclude(include);
    setParams(state => ({
      ...state,
      // The row count can change either way — switching off after the user lifted
      // the exclusion takes rows away — and the page the user is on may not
      // survive that. Reset here rather than leaning on the query change to do
      // it: switching off often rewrites nothing.
      pageIndex: 0,
      query: next
    }),
    // The setting lives in memory, not in the address, so this write is a
    // consequence of it rather than a step of its own. Pushed, one press of back
    // would undo the exclusion while leaving the setting on, and the resources it
    // hides would appear.
    {
      replace: true
    });
  };
  const value = useMemo(() => ({
    includeUnsupportedResources: includeUnsupported,
    setIncludeUnsupportedResources,
    // With the old bar there is no settings panel to hand anything over from,
    // so the filters own the exclusion outright and nothing here adds one.
    filtersOwnUnsupportedExclusion: !NewFilterBar || handedOverToFiltersRef.current
  }),
  // `query` is a dependency twice over: the setter writes the query it reads, and
  // every path that moves the refs below also lands on it or on the flag beside
  // it, so the value is rebuilt in the same render the handover flips.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [NewFilterBar, includeUnsupported, query, setParams]);
  return <InventorySettingsContext.Provider value={value}>
      {children}
    </InventorySettingsContext.Provider>;
};
export const useInventorySettings = () => {
  const context = useContext(InventorySettingsContext);
  if (!context) {
    throw new Error("useInventorySettings must be used within an InventorySettingsProvider");
  }
  return context;
};

/**
 * Whether the Unsupported backup status may be offered as a filter value. For
 * shared filter code, which also renders on pages that have no inventory
 * settings panel to opt in with — there the status is offered as it always was.
 */
export const useIsUnsupportedStatusOffered = () => {
  return useContext(InventorySettingsContext)?.includeUnsupportedResources ?? true;
};